import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { JSONContent } from '@tiptap/core';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useController, useForm, useWatch, type Control } from 'react-hook-form';
import { Navigate, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/api/envelope';
import type { PageCreate } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { ROUTES } from '@/app/routes';
import { RichTextField, type RichTextImages } from '@/components/form/RichTextField';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatusChip } from '@/components/ui/StatusChip';
import { useToast } from '@/components/ui/useToast';

import { HeaderImageField } from './HeaderImageField';
import { LinkedContentField } from './LinkedContentField';
import { PreviewDrawer } from './PreviewDrawer';
import {
  BUTTON_LABELS,
  buttonsFor,
  type EditorButtonId,
  type MoveTarget,
} from './pageActions';
import {
  stateTone,
  stateWord,
  type HeaderImage,
  type PageDetail,
  type PageForm,
  type PageLimits,
} from './pageLabels';
import { formOf, toRequestBody } from './pageForm';
import { pageFieldRefusal, refusalOf } from './pageRefusals';
import { contentRefusal, pageSchema, publishRefusals, type Refusal } from './pageSchemas';
import {
  usePageDetail,
  usePageLimits,
  usePagesOrganizationId,
  useReadPage,
} from './usePages';
import { MOVE_FAILED, usePageWrites } from './usePageWrites';
import { usePagesWritable } from './usePagesWritable';

const EMPTY_FORM: PageForm = { title: '', summary: '', headerImage: null, links: [] };

/** A page's stored document, as the editor takes it. */
function contentOf(page: PageDetail | null): JSONContent | null {
  return page?.content ?? null;
}

/** What Reload says when the newer page could not be read (P17, P21). */
const RELOAD_FAILED = 'The page could not be reloaded. Please try again.';

/**
 * A page this organization does not have (B13): a 404, or a 422 for an
 * address that is no page id at all, as the organization page reads both.
 */
function PageNotFound() {
  return (
    <Box data-testid="page-editor-page" sx={{ maxWidth: 760 }}>
      <PageHeader title="Custom page not found" />
      <ErrorState
        title="This page was not found. It may belong to another organization, or the address may be wrong."
        action={
          <Button
            component={RouterLink}
            to={ROUTES.pages.custom}
            variant="secondary"
            size="sm"
            data-testid="page-editor-back-link"
          >
            Back to Custom Pages
          </Button>
        }
        testId="page-editor-not-found"
      />
    </Box>
  );
}

const BUTTON_TEST_IDS: Record<EditorButtonId, string> = {
  cancel: 'page-editor-cancel-button',
  preview: 'page-editor-preview-button',
  saveDraft: 'page-editor-save-draft-button',
  saveChanges: 'page-editor-save-changes-button',
  publish: 'page-editor-publish-button',
  unpublish: 'page-editor-unpublish-button',
};

/** The buttons that write; they wait for every write and upload (P19). */
const WRITES = new Set<EditorButtonId>([
  'saveDraft',
  'saveChanges',
  'publish',
  'unpublish',
]);

/** A save's server failure: a toast, the input kept (P21). */
const SAVE_FAILED = 'The page could not be saved. Please try again.';

/** Schema-shaped refusals ("Title: ...") onto the form's fields. */
const SERVER_FIELDS = {
  title: 'title',
  summary: 'summary',
  header_image_alt: 'headerImage.alt',
} as const;

interface TitleAndSummaryProps {
  control: Control<PageForm>;
  titleMax: number;
  summaryMax: number;
  disabled: boolean;
}

/**
 * Title and summary, each with its counter against the reply's limits
 * (P3, PB5). Memoised with stable props, so typing in Page Content, which
 * re-renders the page once when it first makes the form dirty, does not
 * redraw them (PNF2).
 */
const TitleAndSummary = memo(function TitleAndSummary({
  control,
  titleMax,
  summaryMax,
  disabled,
}: TitleAndSummaryProps) {
  const title = useController({ control, name: 'title' });
  const summary = useController({ control, name: 'summary' });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Field
        label="Title"
        hint={`${title.field.value.length} / ${titleMax}`}
        error={title.fieldState.error?.message}
        testId="page-editor-title"
      >
        <TextField
          value={title.field.value}
          onChange={title.field.onChange}
          onBlur={title.field.onBlur}
          inputRef={title.field.ref}
          disabled={disabled}
          slotProps={{ htmlInput: { maxLength: titleMax } }}
        />
      </Field>
      <Field
        label="Summary"
        hint={`${summary.field.value.length} / ${summaryMax}`}
        error={summary.fieldState.error?.message}
        testId="page-editor-summary"
      >
        <TextField
          value={summary.field.value}
          onChange={summary.field.onChange}
          onBlur={summary.field.onBlur}
          inputRef={summary.field.ref}
          disabled={disabled}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: summaryMax } }}
        />
      </Field>
    </Box>
  );
});

/**
 * The page editor at `/pages/new` and `/pages/:id` (PB4 to PB9, PB13, P6,
 * P12, P13, P20). One full page: title and summary, the header image, Page
 * Content in the shared rich-text editor, and Linked Content, with the
 * buttons `buttonsFor` gives the page's lifecycle.
 *
 * The content is held outside the form, in a ref the editor reports into,
 * so typing there costs the rest of the page nothing (PNF2); `contentDirty`
 * joins the form's own dirty state for the leave question (I8).
 */
export function PageEditorPage() {
  const { id } = useParams<{ id: string }>();
  const routeId = id ?? null;
  const { writable } = usePagesWritable();

  /*
   * What the editor opened on: a saved page with its limits, or a new page's
   * limits. Held once known, so the form is made once with its values, and
   * the address moving from /pages/new to the page's own (P6) keeps it.
   */
  const [opened, setOpened] = useState<Opened | null>(null);
  const detail = usePageDetail(opened ? null : routeId);
  const newLimits = usePageLimits(!opened && routeId === null && writable);

  /*
   * Another page's address, reached from this one, or another organization
   * switched to under it: open afresh. A first save's move to the page's own
   * address is not another page. Each opening draws a new editor, so New
   * Custom Page after a first save starts empty.
   */
  const organizationId = usePagesOrganizationId();
  const [seen, setSeen] = useState({ routeId, organizationId });
  const [opening, setOpening] = useState(0);
  let ready = opened;
  if (routeId !== seen.routeId || organizationId !== seen.organizationId) {
    setSeen({ routeId, organizationId });
    if (
      opened &&
      (opened.routeId !== routeId || organizationId !== seen.organizationId)
    ) {
      setOpened(null);
      setOpening((count) => count + 1);
      ready = null;
    }
  }
  if (!ready && routeId && detail.data) {
    ready = { routeId, initial: detail.data, limits: detail.data.limits };
    setOpened(ready);
  } else if (!ready && !routeId && newLimits.data) {
    ready = { routeId, initial: null, limits: newLimits.data };
    setOpened(ready);
  }

  // Created here, the page now has its own address; the form stays.
  const onCreated = useCallback(
    (createdId: string) =>
      setOpened((current) => (current ? { ...current, routeId: createdId } : current)),
    [],
  );

  if (ready) {
    return (
      <PageEditor
        key={opening}
        initial={ready.initial}
        limits={ready.limits}
        onCreated={onCreated}
      />
    );
  }

  // Somebody who may only look has nothing to create (P22).
  if (!routeId && !writable) return <Navigate to={ROUTES.pages.custom} replace />;

  // The reads: a saved page, or a new page's limits (PB4, PNF4, B13).
  if (
    routeId &&
    detail.error instanceof ApiError &&
    (detail.error.status === 404 || detail.error.status === 422)
  ) {
    return <PageNotFound />;
  }
  const heading = routeId ? 'Edit Custom Page' : 'New Custom Page';
  if (routeId ? detail.isError : newLimits.isError) {
    return (
      <Box data-testid="page-editor-page" sx={{ maxWidth: 760 }}>
        <PageHeader title={heading} />
        <ErrorState
          title={
            routeId
              ? 'The page could not be loaded.'
              : 'The page editor could not be loaded.'
          }
          onRetry={() => void (routeId ? detail.refetch() : newLimits.refetch())}
          testId="page-editor"
        />
      </Box>
    );
  }
  return (
    <Box data-testid="page-editor-page">
      <PageHeader title={heading} />
      <Box data-testid="page-editor-loading">
        <LinearProgress aria-label="Loading the page" />
      </Box>
    </Box>
  );
}

interface Opened {
  /** The address it was opened at; a first save moves it to the page's own. */
  routeId: string | null;
  initial: PageDetail | null;
  limits: PageLimits;
}

interface PageEditorProps {
  /** The saved page it opened on, or null for a new page. */
  initial: PageDetail | null;
  /** From the reply: every bound the form checks (P3, I5). */
  limits: PageLimits;
  onCreated: (id: string) => void;
}

function PageEditor({ initial, limits, onCreated }: PageEditorProps) {
  const navigate = useNavigate();
  const { writable, readOnlyReason } = usePagesWritable();

  /** The page as last put into the form: as opened, saved, or reloaded. */
  const [page, setPage] = useState<PageDetail | null>(initial);
  const readPage = useReadPage();
  // A write answered 404: the page is gone from this organization (B13).
  const [gone, setGone] = useState(false);

  const [resolver] = useState(() => zodResolver(pageSchema(limits)));
  const form = useForm<PageForm>({
    defaultValues: initial ? formOf(initial) : EMPTY_FORM,
    resolver,
  });
  const { control, reset, setError, setFocus, setValue, getValues, handleSubmit } = form;
  const { isDirty, errors } = form.formState;

  // Page Content, outside the form (PNF2): the editor reports into the ref;
  // `content` is what the editor is made from, changed only by a fill.
  const contentRef = useRef<JSONContent | null>(contentOf(initial));
  const [content, setContent] = useState<JSONContent | null>(contentOf(initial));
  const [contentDirty, setContentDirty] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const contentBox = useRef<HTMLDivElement>(null);

  // Image addresses by key: the detail's signed ones and this session's uploads.
  const addresses = useRef<Record<string, string>>({ ...initial?.images });

  // What a refused write says, where design.md's table puts it.
  const [failure, setFailure] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [linksError, setLinksError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const headerBox = useRef<HTMLDivElement>(null);
  const linksBox = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // An upload's 403: the editor's alert; the writes reread writability (P21).
  const onForbidden = useCallback((error: ApiError) => {
    setFailure(error.serverMessage ?? error.message);
  }, []);
  const writes = usePageWrites({ onForbidden });
  const { upload, uploading, rereadWritability } = writes;

  const [images] = useState<RichTextImages>(() => ({
    address: (key) => addresses.current[key] ?? null,
    upload: async (file) => {
      const stored = await upload('inline', file);
      if (stored.url) addresses.current[stored.key] = stored.url;
      return stored;
    },
  }));

  /** Puts a page into the form, the content and the image addresses (I6). */
  const fill = useCallback(
    (next: PageDetail, { remount }: { remount: boolean }) => {
      Object.assign(addresses.current, next.images);
      reset(formOf(next));
      contentRef.current = contentOf(next);
      setContentDirty(false);
      setContentError(null);
      setPage(next);
      if (remount) {
        setContent(contentOf(next));
        setEditorKey((key) => key + 1);
      }
    },
    [reset],
  );

  // The content's refusal stays until the next save: the editor also reports
  // a change when its own props change, which is no answer to the refusal.
  const onContent = useCallback((value: JSONContent | null) => {
    contentRef.current = value;
    setContentDirty(true);
  }, []);

  const headerImage = useWatch({ control, name: 'headerImage' });
  const onHeaderImage = useCallback(
    (next: HeaderImage | null) => {
      setHeaderError(null);
      setValue('headerImage', next, { shouldDirty: true });
    },
    [setValue],
  );

  // A first save moves to the page's own address once the form shows the
  // saved page, so the leave question is already clear (P6, I8).
  const [created, setCreated] = useState<string | null>(null);
  // Once: `navigate` changes with the address, and a second run would pull a
  // later New Custom Page back to this page.
  const movedTo = useRef<string | null>(null);
  useEffect(() => {
    if (!created || movedTo.current === created) return;
    movedTo.current = created;
    void navigate(ROUTES.pages.edit(created), { replace: true });
  }, [created, navigate]);

  const dirty = isDirty || contentDirty;
  // A page that is gone has nothing left to save, so leaving asks nothing.
  const unsaved = useUnsavedChanges(dirty && !gone);

  const lifecycle = page?.lifecycle ?? 'NEW';
  const view = buttonsFor(lifecycle, writable, dirty);
  const busy = uploading > 0 || writes.anyPending;
  // Set before the first await, so a double click cannot send twice (PNF7).
  const inFlight = useRef(false);

  /** Sets each refusal on its field, focus on the first (P13). */
  function refuse(refusals: Refusal[]) {
    refusals.forEach(({ field, message }) => {
      if (field === 'content') setContentError(message);
      else setError(field, { type: 'manual', message });
    });
    const first = refusals[0]?.field;
    if (first === 'content') {
      contentBox.current?.querySelector<HTMLElement>('[role="textbox"]')?.focus();
    } else if (first) {
      setFocus(first);
    }
  }

  /** What a save would refuse before it is sent: the bounds, and a title. */
  async function saveRefusals(needsPublishFields: boolean): Promise<boolean> {
    const current = getValues();
    const typed = contentRef.current;
    if (needsPublishFields) {
      const refusals = publishRefusals(current, typed, limits);
      if (refusals.length > 0) {
        refuse(refusals);
        return false;
      }
    }
    let valid = false;
    await handleSubmit(
      () => {
        valid = true;
      },
      () => {
        valid = false;
      },
    )();
    if (!valid) return false;
    const tooLong = contentRefusal(typed, limits);
    if (tooLong) {
      refuse([{ field: 'content', message: tooLong }]);
      return false;
    }
    return true;
  }

  /** Puts a refused create or save where design.md's table says (P21, PNF5). */
  function showRefusal(error: unknown, headerSent: boolean) {
    const { kind, sentence } = refusalOf(error);
    // The session's and the organization context's: handled outside the screen.
    if (kind === 'outside') return;
    if (kind === 'notFound') setGone(true);
    else if (kind === 'conflict') setConflict(sentence);
    else if (kind === 'forbidden') {
      setFailure(sentence);
      void rereadWritability();
    } else if (kind === 'invalid') {
      if (!placeFieldRefusal(error as ApiError, headerSent)) {
        const { remainder } = applyServerErrors(
          error as ApiError,
          { setError, setFocus },
          SERVER_FIELDS,
        );
        if (remainder) setFailure(remainder);
      }
    } else toast.show({ message: SAVE_FAILED, severity: 'error' });
  }

  /**
   * Puts a 422 on the field its sentence names, focus there; false when it
   * names none. A save's and a preview's refusals land the same way.
   */
  function placeFieldRefusal(error: ApiError, headerSent: boolean): boolean {
    const placed = pageFieldRefusal(error, { headerSent });
    if (placed?.field === 'title' || placed?.field === 'summary') {
      setError(placed.field, { type: 'server', message: placed.message });
      setFocus(placed.field);
    } else if (placed?.field === 'alt') {
      setError('headerImage.alt', { type: 'server', message: placed.message });
      headerBox.current?.querySelector<HTMLElement>('input:not([type="file"])')?.focus();
    } else if (placed?.field === 'headerImage') {
      setHeaderError(placed.message);
      headerBox.current?.querySelector<HTMLElement>('button')?.focus();
    } else if (placed?.field === 'content') {
      refuse([{ field: 'content', message: placed.message }]);
    } else if (placed?.field === 'links') {
      setLinksError(placed.message);
      linksBox.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
    } else {
      return false;
    }
    return true;
  }

  /**
   * Preview (PB11, P16): the form as it stands, rendered by the server in the
   * drawer; nothing is saved. A refusal naming a field shows there too.
   */
  const [previewing, setPreviewing] = useState<PageCreate | null>(null);
  function openPreview() {
    setPreviewing(toRequestBody(getValues(), contentRef.current));
  }
  function onPreviewRefused(error: ApiError) {
    // Only a field's refusal is copied out: nothing the person saved was refused.
    placeFieldRefusal(error, Boolean(previewing?.header_image_key));
  }

  function clearRefusals() {
    setFailure(null);
    setConflict(null);
    setHeaderError(null);
    setLinksError(null);
    setContentError(null);
  }

  /**
   * Sends the form (PB9, B1, B5): a new page is created once and moves to its
   * own address; a saved one sends the version it was loaded from (I6). The
   * reply refills the form, so the next save sends the new version and the
   * form is clean (I8). Answers the saved page, or null when it was refused.
   */
  async function persist(): Promise<PageDetail | null> {
    const body = toRequestBody(getValues(), contentRef.current);
    try {
      const saved = page
        ? await writes.save.mutateAsync({
            id: page.id,
            body: { ...body, expected_version: page.version },
            live: page.lifecycle === 'PUBLISHED',
          })
        : await writes.create.mutateAsync(body);
      fill(saved, { remount: false });
      const said = page ? 'Saved.' : 'Draft saved.';
      toast.show({ message: said });
      setAnnouncement(said);
      if (!page) {
        onCreated(saved.id);
        setCreated(saved.id);
      }
      return saved;
    } catch (error) {
      showRefusal(error, Boolean(body.header_image_key));
      return null;
    }
  }

  /** Save Draft and Save Changes. */
  async function save() {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      clearRefusals();
      if (!(await saveRefusals(lifecycle === 'PUBLISHED'))) return;
      await persist();
    } finally {
      inFlight.current = false;
    }
  }

  /** Puts a refused move above the form, in the backend's words (P18, P21, PNF5). */
  function showMoveRefusal(error: unknown) {
    const { kind, sentence } = refusalOf(error);
    if (kind === 'outside') return;
    if (kind === 'notFound') setGone(true);
    else if (kind === 'forbidden') {
      setFailure(sentence);
      void rereadWritability();
    } else if (kind === 'conflict' || kind === 'invalid') setFailure(sentence);
    else toast.show({ message: MOVE_FAILED, severity: 'error' });
  }

  /** Moves the page, refilling the form from the reply (B6, PB10). */
  async function move(target: PageDetail, to: MoveTarget, said: string) {
    try {
      const moved = await writes.transition.mutateAsync({ id: target.id, to });
      fill(moved, { remount: false });
      toast.show({ message: said });
      setAnnouncement(said);
    } catch (error) {
      showMoveRefusal(error);
    }
  }

  /**
   * Publish Page (PB10, P12): the form is saved first when it has changes or
   * the page is new, then published. If the save went through and the
   * publish is refused, the page is a saved draft at its own address and the
   * alert says why (P18).
   */
  async function publish() {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      clearRefusals();
      if (!(await saveRefusals(true))) return;
      const target = !page || dirty ? await persist() : page;
      if (target) await move(target, 'PUBLISHED', 'Published.');
    } finally {
      inFlight.current = false;
    }
  }

  /** Unpublish, offered only with no unsaved changes (P12). */
  async function unpublish() {
    if (inFlight.current || !page) return;
    inFlight.current = true;
    try {
      clearRefusals();
      await move(page, 'INACTIVE', 'Unpublished.');
    } finally {
      inFlight.current = false;
    }
  }

  /** After a 409: the newer page, into the form and the content (P17). */
  async function reload() {
    if (!page) return;
    try {
      fill(await readPage(page.id), { remount: true });
      setConflict(null);
    } catch (error) {
      const { kind } = refusalOf(error);
      if (kind === 'outside') return;
      if (kind === 'notFound') setGone(true);
      else toast.show({ message: RELOAD_FAILED, severity: 'error' });
    }
  }

  const press: Record<EditorButtonId, () => void> = {
    cancel: () => void navigate(ROUTES.pages.custom),
    preview: openPreview,
    saveDraft: () => void save(),
    saveChanges: () => void save(),
    publish: () => void publish(),
    unpublish: () => void unpublish(),
  };

  if (gone) return <PageNotFound />;

  return (
    <Box data-testid="page-editor-page" sx={{ maxWidth: 860 }}>
      <PageHeader
        title={page ? 'Edit Custom Page' : 'New Custom Page'}
        titleAdornment={
          <StatusChip
            label={stateWord(page?.lifecycle ?? 'DRAFT')}
            tone={stateTone(page?.lifecycle ?? 'DRAFT')}
            testId="page-editor-status-chip"
          />
        }
        action={
          <Box
            data-testid="page-editor-actions"
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
          >
            {view.buttons.map((each) => (
              <Button
                key={each.id}
                variant={
                  each.id === 'cancel' || each.id === 'preview' ? 'secondary' : 'primary'
                }
                disabled={each.disabled || (WRITES.has(each.id) && busy)}
                title={each.reason ?? undefined}
                onClick={press[each.id]}
                data-testid={BUTTON_TEST_IDS[each.id]}
              >
                {BUTTON_LABELS[each.id]}
              </Button>
            ))}
          </Box>
        }
      />

      {view.buttons
        .filter((each) => each.reason)
        .map((each) => (
          <Typography
            key={each.id}
            variant="body2"
            color="text.secondary"
            data-testid={`${BUTTON_TEST_IDS[each.id]}-reason`}
            sx={{ mb: 2 }}
          >
            {each.reason}
          </Typography>
        ))}

      {view.alert ? (
        <Alert severity="info" data-testid="page-editor-removed-alert" sx={{ mb: 3 }}>
          {view.alert}
        </Alert>
      ) : null}
      {readOnlyReason ? (
        <Alert
          severity="warning"
          role="alert"
          data-testid="page-editor-read-only-alert"
          sx={{ mb: 3 }}
        >
          {readOnlyReason}
        </Alert>
      ) : null}
      {conflict ? (
        <Alert
          severity="error"
          data-testid="page-editor-conflict-alert"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void reload()}
              data-testid="page-editor-reload-button"
            >
              Reload
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {conflict}
        </Alert>
      ) : null}
      {failure ? (
        <Alert severity="error" data-testid="page-editor-error-alert" sx={{ mb: 3 }}>
          {failure}
        </Alert>
      ) : null}
      <LiveRegion message={announcement} />

      <Box
        component="form"
        noValidate
        onSubmit={(event) => event.preventDefault()}
        sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        <TitleAndSummary
          control={control}
          titleMax={limits.titleMaxLength}
          summaryMax={limits.summaryMaxLength}
          disabled={view.readOnly}
        />

        <Box ref={headerBox}>
          <HeaderImageField
            value={headerImage}
            onChange={onHeaderImage}
            upload={upload}
            altMaxLength={limits.altMaxLength}
            altError={errors.headerImage?.alt?.message}
            serverError={headerError}
            disabled={view.readOnly}
          />
        </Box>

        <Box ref={contentBox}>
          <RichTextField
            key={editorKey}
            label="Page Content"
            hint="Headings, bold, italic, lists, links, images and call-to-action buttons."
            value={content}
            onChange={onContent}
            error={contentError ?? undefined}
            disabled={view.readOnly}
            images={images}
            maxLength={limits.richTextMaxLength}
            testId="page-editor-content"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="page-editor-content-note"
          >
            Scripts and embedded code are not supported
          </Typography>
        </Box>

        <Box ref={linksBox}>
          <LinkedContentField
            control={control}
            maxLinkedDocuments={limits.maxLinkedDocuments}
            error={linksError ?? errors.links?.message ?? errors.links?.root?.message}
            disabled={view.readOnly}
          />
        </Box>
      </Box>

      <UnsavedChangesDialog {...unsaved} />
      <PreviewDrawer
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        body={previewing}
        request={writes.preview.mutateAsync}
        onRefused={onPreviewRefused}
      />
    </Box>
  );
}
